import { Link, createFileRoute } from "@tanstack/react-router";
import { PageImageGallery } from "@/components/page-image-gallery";
import { ArrowRight } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { NewsHero } from "@/components/news-hero";
import { NEWS_ARTICLES } from "@/content/news";
import { getAuthor } from "@/lib/authors";
import { ogImageMeta } from "@/lib/og";
import { SITE } from "@/lib/site";

const TITLE = "Beste Casino Betaalnieuws & Analyses | Casino Betalingen";
const DESCRIPTION =
  "Ontdek het beste casino betaalnieuws en onze nieuwste analyses over wallets, instant payments, regels en eigen storttests. Bijgewerkt november 2026.";

export const Route = createFileRoute("/nieuws/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
      ...ogImageMeta("nieuws"),
      { property: "og:url", content: `${SITE.url}/nieuws/` },
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/nieuws/` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Betaalnieuws",
          description: DESCRIPTION,
          inLanguage: "nl-NL",
          url: `${SITE.url}/nieuws/`,
          publisher: { "@id": `${SITE.url}/#organization` },
        }),
      },
    ],
  }),
  component: NewsIndexPage,
});

function NewsIndexPage() {
  const [lead, ...rest] = NEWS_ARTICLES;

  return (
    <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-success">Redactie</p>
      <h1 className="mt-2 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
        Beste casino betaalnieuws en analyses
      </h1>
      <p className="mt-3 max-w-2xl text-lg text-muted-foreground">
        Wat er verandert aan betalen bij online casino's — wallets, instant payments, regels en de
        cijfers uit onze eigen storttests. Elk stuk heeft een auteur en een factchecker.
      </p>

      {lead ? (
        <Card className="card-surface mt-10 p-6 sm:p-8">
          <NewsHero
            className="mb-6"
            image={lead.heroImage}
            methods={lead.heroMethods}
            alt={lead.heroAlt ?? lead.title}
            eager
          />
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="secondary">{lead.category}</Badge>
            <time dateTime={lead.publishedAt} className="text-xs text-muted-foreground">
              {lead.publishedLabel}
            </time>
          </div>
          <h2 className="mt-4 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            <Link
              to="/nieuws/$slug/"
              params={{ slug: lead.slug }}
              className="hover:underline"
            >
              {lead.title}
            </Link>
          </h2>
          <p className="mt-3 max-w-3xl text-[15px] leading-7 text-muted-foreground">{lead.description}</p>
          <p className="mt-4 text-xs text-muted-foreground">
            {getAuthor(lead.authorSlug)?.name} · {lead.readingMinutes} min. lezen
          </p>
          <Link
            to="/nieuws/$slug/"
            params={{ slug: lead.slug }}
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-success hover:underline"
          >
            Lees de analyse <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </Card>
      ) : null}

      {rest.length > 0 ? (
        <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((a) => (
            <Card key={a.slug} className="card-surface flex flex-col p-5">
              <NewsHero
                className="mb-4"
                image={a.heroImage}
                methods={a.heroMethods}
                alt={a.heroAlt ?? a.title}
                compact
              />
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">{a.category}</Badge>
                <time dateTime={a.publishedAt} className="text-xs text-muted-foreground">
                  {a.publishedLabel}
                </time>
              </div>
              <h2 className="mt-3 text-base font-semibold leading-6 text-foreground">
                <Link
                  to="/nieuws/$slug/"
                  params={{ slug: a.slug }}
                  className="hover:underline"
                >
                  {a.title}
                </Link>
              </h2>
              <p className="mt-2 line-clamp-4 text-sm leading-6 text-muted-foreground">{a.description}</p>
            </Card>
          ))}
        </div>
      ) : null}

      <p className="mt-10 text-sm text-muted-foreground">
        Op zoek naar de vergelijkingen zelf? Bekijk{" "}
        <Link to="/betaalmethoden/" className="underline underline-offset-4">alle betaalmethoden</Link> of
        gebruik de <Link to="/casinozoeker/" className="underline underline-offset-4">casinozoeker</Link>.
      </p>
          <PageImageGallery path="/nieuws" title="Betaalnieuws in beeld" />
    </main>
  );
}
